import parseHTMLToReact from "html-react-parser";
import { Flex, Loading, P } from "orcs-design-system";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Liquid } from "liquidjs";

import { createHtmlParsingOptions } from "../utils/dashboardHtmlParsingOptions";
import { useDashboardData } from "./DashboardDataContext";
import { sanitizeDashboardHtml } from "./sanitizeHtml";
import { sanitizeCss } from "./sanitizers";

const engine = new Liquid({ strictVariables: false, strictFilters: false });

const getScrollableAncestor = (el) => {
  let node = el?.parentElement;
  while (node) {
    const { overflow, overflowY } = getComputedStyle(node);
    if (/(auto|scroll)/.test(overflow + overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return document.documentElement;
};

const useScrollPreservation = (contentKey, layoutKey) => {
  const containerRef = useRef(null);
  const scrollRatio = useRef(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const scrollable = getScrollableAncestor(el);
    if (!scrollable) {
      return;
    }

    const onScroll = () => {
      const max = scrollable.scrollHeight - scrollable.clientHeight;
      scrollRatio.current = max > 0 ? scrollable.scrollTop / max : 0;
    };
    scrollable.addEventListener("scroll", onScroll, { passive: true });
    return () => scrollable.removeEventListener("scroll", onScroll);
  }, [layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const scrollable = getScrollableAncestor(el);
    if (!scrollable) {
      return;
    }

    const max = scrollable.scrollHeight - scrollable.clientHeight;
    scrollable.scrollTop = Math.round(scrollRatio.current * max);
  }, [contentKey, layoutKey]);

  return containerRef;
};

const DashboardRenderer = () => {
  const {
    queryData,
    loading,
    error,
    effectiveCss,
    effectiveTemplate,
    isEditing,
  } = useDashboardData();

  const renderedContent = useMemo(() => {
    if (!effectiveTemplate || Object.keys(queryData).length === 0) {
      return null;
    }

    try {
      const rawHtml = engine.parseAndRenderSync(effectiveTemplate, queryData);
      const sanitizedHtml = sanitizeDashboardHtml(rawHtml);

      return parseHTMLToReact(sanitizedHtml.trim(), createHtmlParsingOptions());
    } catch (e) {
      console.error("Liquid render error:", e);
      return <P>Error rendering dashboard template.</P>;
    }
  }, [effectiveTemplate, queryData]);

  const safeCss = useMemo(
    () => sanitizeCss(effectiveCss || ""),
    [effectiveCss]
  );

  const containerRef = useScrollPreservation(renderedContent, isEditing);

  if (!renderedContent && loading) {
    return (
      <Flex justifyContent="center" alignItems="center" py="l">
        <Loading />
      </Flex>
    );
  }

  if (error) {
    return <P color="danger">Error loading dashboard data: {error.message}</P>;
  }

  return (
    <div ref={containerRef}>
      <style>{safeCss}</style>
      {renderedContent}
    </div>
  );
};

export default DashboardRenderer;
